import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { IS_MOCK_MODE, STORAGE_KEYS, readJSON, writeJSON } from '@/lib/mockMode'
import { TIERS } from '@/data/pricing'
import { trackEvent } from '@/utils/analytics'

// ── Tier definitions ───────────────────────────────────────────
// Prices and page quotas come from src/data/pricing.js (single source —
// pricing v2). This map adds app-side concerns: badges + feature flags.
export const TIER_ORDER = ['free', 'solo', 'business', 'enterprise']

const TIER_BADGES = {
  free: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  solo: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  business: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  enterprise: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
}

// Boolean capability matrix for in-app feature gates (ProFeatureGate /
// ProShowcaseModal). customFields is the Business+ differentiator; creditPacks
// was retired in pricing v3 (consent-gated metered overage replaced it).
// emailDelivery = on-demand "email my data" to the registered address (Solo+).
// emailDeliveryPro = custom delivery address + auto-send on completion (Business+).
const TIER_FEATURES = {
  free: { jsonExport: true, csvExport: true, customFields: false, bulkExport: false, emailSupport: false, apiAccess: false, sso: false, emailDelivery: false, emailDeliveryPro: false, seats: false },
  solo: { jsonExport: true, csvExport: true, customFields: false, bulkExport: false, emailSupport: false, apiAccess: false, sso: false, emailDelivery: true, emailDeliveryPro: false, seats: false },
  business: { jsonExport: true, csvExport: true, customFields: true, bulkExport: true, emailSupport: true, apiAccess: false, sso: false, emailDelivery: true, emailDeliveryPro: true, seats: false },
  enterprise: { jsonExport: true, csvExport: true, customFields: true, bulkExport: true, emailSupport: true, apiAccess: true, sso: false /* Enterprise Custom */, emailDelivery: true, emailDeliveryPro: true, seats: true },
}

const _defs = Object.fromEntries(
  TIERS.map((t) => [
    t.id,
    Object.freeze({
      id: t.id,
      label: t.name,
      price: t.monthly,
      priceLabel: `$${t.monthly}`,
      annualPrice: t.annual,
      annualPriceLabel: t.annual === null ? null : `$${t.annual}`,
      pagesLimit: t.pages,
      badgeClass: TIER_BADGES[t.id],
      features: Object.freeze(TIER_FEATURES[t.id]),
    }),
  ]),
)
// Legacy alias: subscribers stored as 'team' (pre-rename) resolve to the
// enterprise definition instead of silently resetting to free.
_defs.team = _defs.enterprise
export const TIER_DEFINITIONS = Object.freeze(_defs)

const DEFAULT_PLAN = Object.freeze({
  tier: 'free',
  status: 'active',
  billingInterval: 'month', // 'month' | 'year'
  startedAt: null,
  endsAt: null,
  polarSubscriptionId: null,
  polarCustomerId: null,
  billingRail: 'none', // 'polar' | 'apple' | 'none'
})

function loadStoredPlan() {
  const stored = readJSON(STORAGE_KEYS.plan, null)
  if (stored && TIER_DEFINITIONS[stored.tier]) return { ...DEFAULT_PLAN, ...stored }
  return { ...DEFAULT_PLAN }
}

function persistPlan(plan) {
  writeJSON(STORAGE_KEYS.plan, plan)
}

export const usePlanStore = defineStore('plan', () => {
  const initial = loadStoredPlan()

  const tier = ref(initial.tier)
  const status = ref(initial.status)
  const billingInterval = ref(initial.billingInterval)
  const startedAt = ref(initial.startedAt)
  const endsAt = ref(initial.endsAt)
  const polarSubscriptionId = ref(initial.polarSubscriptionId)
  const polarCustomerId = ref(initial.polarCustomerId)
  const billingRail = ref(initial.billingRail)

  const definition = computed(() => TIER_DEFINITIONS[tier.value] || TIER_DEFINITIONS.free)
  const isFree = computed(() => tier.value === 'free')
  const isPaid = computed(() => tier.value !== 'free')
  // Apple-rail subscribers cannot be managed from the web: StoreKit owns the
  // subscription, so the Polar portal would be a dead end for them.
  const isAppleBilled = computed(() => billingRail.value === 'apple')
  const pagesLimit = computed(() => definition.value.pagesLimit)
  const isCancelling = computed(() => status.value === 'cancelled' && endsAt.value && new Date(endsAt.value) > new Date())
  const features = computed(() => definition.value.features)

  function _persist() {
    persistPlan({
      tier: tier.value,
      status: status.value,
      billingInterval: billingInterval.value,
      startedAt: startedAt.value,
      endsAt: endsAt.value,
      polarSubscriptionId: polarSubscriptionId.value,
      polarCustomerId: polarCustomerId.value,
      billingRail: billingRail.value,
    })
  }

  function hasFeature(name) {
    return !!features.value[name]
  }

  // Real mode: applied from the backend's /billing/quota response — the
  // server (driven by Polar webhooks) is the source of truth.
  function applyServerState({ tier: t, billingInterval: bi, status: s, endsAt: e, polarCustomerId: pc, polarSubscriptionId: ps, billingRail: br }) {
    // Count a genuine paid upgrade: server (Polar webhook) flips us from the
    // free tier to a paid one. Fires once on the transition, not on every sync.
    const wasFree = tier.value === 'free'
    if (t && TIER_DEFINITIONS[t]) tier.value = t
    if (wasFree && tier.value !== 'free') trackEvent('pro_upgrade')
    if (bi) billingInterval.value = bi
    if (s) status.value = s === 'cancelled' || s === 'canceled' ? 'cancelled' : s
    endsAt.value = e ?? null
    if (pc !== undefined) polarCustomerId.value = pc
    if (ps !== undefined) polarSubscriptionId.value = ps
    if (br !== undefined) billingRail.value = br || 'none'
    _persist()
  }

  // ── Dev / mock-mode mutations ─────────────────────────────────
  // These let DevTierToggle cycle tiers locally. Real Polar checkout
  // ships in Session 7; that handler calls the same setTier() in
  // response to a webhook event.
  function setTier(nextTier, interval = 'month') {
    if (!TIER_DEFINITIONS[nextTier]) return
    tier.value = nextTier
    status.value = 'active'
    billingInterval.value = nextTier === 'free' ? 'month' : interval
    startedAt.value = nextTier === 'free' ? null : new Date().toISOString()
    endsAt.value = null
    _persist()
  }

  function startCancellation() {
    if (tier.value === 'free') return
    status.value = 'cancelled'
    // Mock: Polar would normally return endsAt; fake one 30 days out.
    endsAt.value = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    _persist()
  }

  function downgradeToFree() {
    tier.value = 'free'
    status.value = 'active'
    billingInterval.value = 'month'
    startedAt.value = null
    endsAt.value = null
    polarSubscriptionId.value = null
    _persist()
  }

  return {
    tier,
    status,
    billingInterval,
    billingRail,
    startedAt,
    endsAt,
    polarSubscriptionId,
    polarCustomerId,
    definition,
    pagesLimit,
    isFree,
    isPaid,
    isAppleBilled,
    isCancelling,
    features,
    isMockMode: IS_MOCK_MODE,
    hasFeature,
    applyServerState,
    setTier,
    startCancellation,
    downgradeToFree,
    TIER_ORDER,
    TIER_DEFINITIONS,
  }
})
